import { readFile } from 'fs';
import { join } from 'path';
import { FioriElementsVersion } from '@sap/ux-specification-types';
import { SchemaType } from '@sap/ux-specification-types';

/**
 * Return the path to the generic JSON schema.
 *
 * @param version - Fiori elements version
 * @param schemaType - Type of the schema, e.g Application, ListReport, ObjectPage, ...
 * @param directory - The base directory to resolve the schema path
 * @returns the path to the generic JSON schema
 */
export const getPathToGenericSchema = (
    version: FioriElementsVersion,
    schemaType: SchemaType,
    directory: string = __dirname
): string | undefined => {
    let fileName: string;
    switch (schemaType) {
        case SchemaType.Application: {
            fileName = `${SchemaType.Application}${version.toUpperCase()}.json`;
            break;
        }
        case SchemaType.FPMCustomPage: {
            // Use building blocks generic schema for FPM custom pages
            fileName = `${SchemaType.BuildingBlocks}Config.json`;
            break;
        }
        default: {
            fileName = `${schemaType}Config.json`;
            break;
        }
    }
    if (directory.endsWith('dist')) {
        return join(directory, 'schemas', version, fileName);
    } else if (directory.endsWith(join('specification', 'packages', 'specification'))) {
        return join(directory, 'dist', 'schemas', version, fileName);
    } else if (directory.endsWith(join('src', 'specification'))) {
        return join(directory, '..', '..', 'dist', 'schemas', version, fileName);
    } else if (directory.indexOf('node_modules') > -1) {
        const moduleRoot = directory.substring(0, directory.indexOf('node_modules') + 12);
        return join(moduleRoot, '@sap', 'ux-specification', 'dist', 'schemas', version, fileName);
    }
    return undefined;
};

/**
 * Reads a JSON schema from the specified file path.
 *
 * @param path - The path to the schema file.
 * @returns A promise that resolves to the parsed JSON object.
 */
const readJsonSchema = async (path: string): Promise<JSON> => {
    return new Promise((resolve, reject): void => {
        readFile(path, { encoding: 'utf8' }, (err, data): void => {
            if (err) {
                reject(err);
            } else {
                resolve(JSON.parse(data));
            }
        });
    });
};

/**
 * Return the generic schema for a given version and type.
 *
 * @param version - Fiori elements version, e.g. v2 or v4
 * @param schemaType - Type of the schema, e.g Application, ListReport, ObjectPage, ...
 * @returns the generic schema for the given Fiori Elements version and schema type
 */
export const getGenericSchema = async (version: FioriElementsVersion, schemaType: SchemaType): Promise<JSON> => {
    try {
        const path = getPathToGenericSchema(version || FioriElementsVersion.v4, schemaType, __dirname);
        return await readJsonSchema(path);
    } catch (error) {
        throw new Error(`No schema available for type: ${schemaType}, version: ${version}. Error was ${error.message}`);
    }
};
